import React, { useEffect, useState } from "react";
import { Sunrise, Send, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { API, fmtErr, money } from "../lib/api";
import { useLang } from "../lib/i18n";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

export function DigestCard({ managerUp, isOwner }) {
  const { t } = useLang();
  const [d, setD] = useState(null);
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => API.get("/shop/digest/latest").then((r) => { setD(r.data); setPhone(r.data.digest_phone || ""); }).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!d) return null;
  const p = d.preview;
  const sendNow = async () => {
    setBusy(true);
    try { await API.post("/shop/digest/send-now"); toast.success(t("dig.sent")); load(); } catch (err) { toast.error(fmtErr(err)); } finally { setBusy(false); }
  };
  const savePhone = async () => {
    try { await API.patch("/shop/profile", { digest_phone: phone || null }); toast.success(t("msg.saved")); load(); } catch (err) { toast.error(fmtErr(err)); }
  };
  const toggle = async () => {
    try { await API.patch("/shop/profile", { digest_enabled: !d.digest_enabled }); load(); } catch (err) { toast.error(fmtErr(err)); }
  };
  const digits = (phone || "").replace(/[^0-9]/g, "");
  const waHref = `https://wa.me/${digits.length === 8 ? `670${digits}` : digits}?text=${encodeURIComponent(d.preview_text)}`;
  const statusOf = (ch) => d.latest?.deliveries?.filter((x) => x.channel === ch).map((x) => x.status)[0];
  return (
    <div className="bg-[#1E3A5F] text-white rounded-xl p-5" data-testid="digest-card">
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <Sunrise className="h-5 w-5 text-amber-300" />
        <h3 className="font-heading text-base font-medium">{t("dig.title")} · {p.date}</h3>
        <span className="text-xs text-white/60">{d.latest ? `${t("dig.last_sent")} ${d.latest.created_at.slice(0, 16).replace("T", " ")} (${d.latest.trigger})` : t("dig.never")}</span>
        <div className="flex-1" />
        {isOwner && (
          <label className="flex items-center gap-1.5 text-xs cursor-pointer">
            <input type="checkbox" checked={d.digest_enabled !== false} onChange={toggle} data-testid="digest-enabled-toggle" />{t("dig.enabled")}
          </label>
        )}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
        <div><div className="text-white/60 text-xs">{t("dash.overdue")}</div><div className="font-mono text-lg" data-testid="digest-overdue">{p.overdue_count} · {money(p.overdue_total)}</div></div>
        <div><div className="text-white/60 text-xs">{t("prep.title")}</div><div className="font-mono text-lg">{p.sale_eligible}</div></div>
        <div><div className="text-white/60 text-xs">{t("alerts.due_soon")} (7d)</div><div className="font-mono text-lg">{p.due_soon}</div></div>
        <div><div className="text-white/60 text-xs">{t("alerts.notice_deadlines")}</div><div className="font-mono text-lg text-amber-300">{p.expired_notices}</div></div>
      </div>
      {p.top_overdue.length > 0 && (
        <ul className="mt-3 text-xs text-white/80 space-y-0.5" data-testid="digest-top-overdue">
          {p.top_overdue.map((o) => <li key={o.loan_number}><span className="font-mono">{o.loan_number}</span> · {o.customer_name} · {o.days_overdue}d · {money(o.total_due)}</li>)}
        </ul>
      )}
      {managerUp && (
        <div className="flex flex-wrap items-center gap-2 mt-4">
          <Button size="sm" data-testid="digest-send-now" onClick={sendNow} disabled={busy} className="bg-white text-[#1E3A5F] hover:bg-slate-100 h-8 text-xs"><Send className="h-3.5 w-3.5 mr-1" />{t("dig.send_now")}</Button>
          <a href={waHref} target="_blank" rel="noreferrer" data-testid="digest-whatsapp" className={`inline-flex items-center gap-1 h-8 px-3 rounded-md text-xs bg-green-500 hover:bg-green-600 ${!digits ? "opacity-40 pointer-events-none" : ""}`}><MessageCircle className="h-3.5 w-3.5" />WhatsApp</a>
          {isOwner && (
            <>
              <Input data-testid="digest-phone-input" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder={t("dig.phone_ph")} className="h-8 w-40 text-xs bg-white text-slate-900" />
              <Button size="sm" variant="outline" className="h-8 text-xs bg-transparent border-white/40 text-white hover:bg-white/10" onClick={savePhone} data-testid="digest-phone-save">{t("common.save")}</Button>
            </>
          )}
          <span className="text-[11px] text-white/60 ml-auto">
            email: {statusOf("email") || "—"} · whatsapp: {statusOf("whatsapp") || "—"}
            {(statusOf("email") === "not_configured" || statusOf("whatsapp") === "not_configured") && ` · ${t("dig.not_configured")}`}
          </span>
        </div>
      )}
    </div>
  );
}
